import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { identityData, categoryStyles } from '../constants/identities';

interface IdentityExpansionModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: string;
  onSave: (identities: string[]) => void;
  initialSelected: string[];
}

export function IdentityExpansionModal({
  isOpen,
  onClose,
  category,
  onSave,
  initialSelected,
}: IdentityExpansionModalProps) {
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [searchQuery, setSearchQuery] = useState('');

  const style = categoryStyles[category] || categoryStyles['Interests'];
  const identities = [...(identityData[category] || [])].sort((a, b) => a.localeCompare(b));

  const filteredIdentities = identities.filter((identity) =>
    identity.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleIdentity = (identity: string) => {
    setSelected((prev) => {
      if (prev.includes(identity)) {
        return prev.filter((i) => i !== identity);
      }
      if (prev.length >= 10) {
        toast.error('You can select up to 10 identities');
        return prev;
      }
      return [...prev, identity];
    });
  };

  const handleSave = () => {
    onSave(selected);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-[90%] max-w-sm bg-light-pattern dark:bg-[#1a1a1a] rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[50vh]"
        >
          {/* Header — accent border matches category colour */}
          <div
            className="p-4 border-b border-black/5 dark:border-white/5 bg-light-pattern dark:bg-[#1a1a1a] flex items-center justify-between shrink-0"
            style={{ borderTopWidth: 3, borderTopStyle: 'solid', borderImage: `${style.gradient} 1` }}
          >
            <h2
              className="text-lg font-bold bg-clip-text text-transparent"
              style={{ backgroundImage: style.gradient }}
            >
              {category}
            </h2>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
            >
              <X className="w-4 h-4 text-black dark:text-white" />
            </motion.button>
          </div>

          {/* Search */}
          <div className="p-3 border-b border-black/5 dark:border-white/5 shrink-0 bg-light-pattern dark:bg-[#1a1a1a]">
            <Input
              placeholder={`Search ${category.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-black/5 dark:bg-white/5 border-transparent text-black dark:text-white h-9 text-sm rounded-lg focus:bg-transparent"
              style={{ '--tw-ring-color': style.gradient } as React.CSSProperties}
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="p-4">
              <div className="flex flex-wrap gap-2 justify-start">
                {filteredIdentities.map((identity) => {
                  const isSelected = selected.includes(identity);
                  return (
                    <motion.button
                      key={identity}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => toggleIdentity(identity)}
                      className={`px-2 py-[2px] rounded-full border text-[10px] transition-all flex items-center justify-center ${
                        isSelected
                          ? 'text-[#a1a1a1] font-bold shadow-sm border-transparent'
                          : `${style.bg} ${style.border} ${style.text}`
                      }`}
                      style={isSelected ? { backgroundImage: style.gradient, textShadow: '0 1px 3px rgba(0,0,0,0.8)' } : undefined}
                    >
                      <div className="flex items-center gap-1">
                        {isSelected && <Check className="w-2.5 h-2.5" />}
                        <span>{identity}</span>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-black/5 dark:border-white/5 bg-light-pattern dark:bg-[#1a1a1a] flex items-center justify-between shrink-0">
            <p className="text-xs font-medium text-black/60 dark:text-white/60">
              {selected.length} selected
            </p>
            <div className="flex gap-2">
              <Button
                onClick={onClose}
                variant="ghost"
                className="h-9 px-3 text-xs"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="h-9 px-4 hover:opacity-90 text-white text-xs font-bold rounded-lg border-none"
                style={{ backgroundImage: style.gradient }}
              >
                Save ({selected.length})
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
